import { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Switch,
  Typography
} from "@mui/material";
import { SyncableField } from "../../components/lifecycle/warehouseSync/utils";
import { canBulkSyncWarehouse } from "../../auth/permissions";
import { useRoles } from "../../auth/useRoles";
import { useWarehouseSyncPreview } from "./dataHooks/warehouseSyncPreviewQueries";
import {
  describeApplyError,
  useApplyWarehouseSync,
  useRefreshSyncedOverviews
} from "./dataHooks/applyWarehouseSyncQueries";
import { SyncTable } from "./syncTable";
import {
  PreviewRow,
  Selection,
  acceptedFieldCount,
  buildApplyJobs,
  buildPreviewRows,
  needsAttention,
  selectAllActionable,
  toggleField,
  toggleRow
} from "./rows";

type ApplyFailure = {
  /** The entity, not the row: a country fails as a whole even when several parties were accepted. */
  label: string;
  /** See describeApplyError. Shown next to the name, because "failed" alone is not actionable. */
  reason: string;
};

type ApplyOutcome = {
  applied: number;
  failed: ApplyFailure[];
};

/**
 * Bulk warehouse sync (ADHO-490). The single-counterparty version of this lives in the Compliance
 * lifecycle dialog; this is the same comparison run across everything that carries a Catalyst ID,
 * for Compliance and Risk to sweep all the books at once.
 *
 * Applying writes through the ordinary update endpoints, one entity at a time, so there is no bulk
 * write path on the backend and nothing here bypasses the normal save.
 *
 * Countries appear once per party, because that is how many Catalyst IDs a country has, but they are
 * written once: see buildApplyJobs. So the row count and the write count differ here, and the
 * progress bar counts writes.
 */
export const WarehouseSync: FC = () => {
  const roles = useRoles();
  const canSync = canBulkSyncWarehouse(roles);

  const preview = useWarehouseSyncPreview();
  const applySync = useApplyWarehouseSync();
  const refreshOverviews = useRefreshSyncedOverviews();

  const [selection, setSelection] = useState<Selection>({});
  const [onlyDifferences, setOnlyDifferences] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(0);
  const [outcome, setOutcome] = useState<ApplyOutcome | null>(null);

  const rows = useMemo(
    () => buildPreviewRows(preview.data?.items ?? []),
    [preview.data]
  );

  // Pre-tick every real difference whenever a new preview lands, same as the dialog. Anything the
  // user unticked belonged to the previous data set.
  //
  // The apply result is deliberately not cleared here. Applying refetches, which lands new rows and
  // would wipe the "applied to N counterparties" message before it could be read.
  useEffect(() => {
    setSelection(selectAllActionable(rows));
  }, [rows]);

  const visibleRows = onlyDifferences ? rows.filter(needsAttention) : rows;

  const jobs = buildApplyJobs(rows, selection);
  const fieldCount = acceptedFieldCount(rows, selection);

  const handleApply = async () => {
    setConfirmOpen(false);
    setApplying(true);
    setApplied(0);
    setOutcome(null);

    const failed: ApplyFailure[] = [];
    let succeeded = 0;

    // Sequential on purpose: each update is a read-modify-write on the entity blob plus a summary
    // table upsert plus a queue message. A failure on one is reported and the sweep continues, so one
    // bad entity does not strand the rest.
    for (const job of jobs) {
      try {
        await applySync.mutateAsync(job.variables);
        succeeded++;
      } catch (error) {
        // Logged in full as well as summarised: the status is what the user needs, the rest is what
        // whoever debugs the next one needs, and a sweep that says only "failed for 1" leaves both
        // with nothing to go on.
        console.error(
          `Warehouse sync: applying ${job.variables.entityType} ${job.variables.id} (${job.label}) failed`,
          error
        );
        failed.push({ label: job.label, reason: describeApplyError(error) });
      }

      setApplied((count) => count + 1);
    }

    setApplying(false);
    setOutcome({ applied: succeeded, failed });

    // Once, now that the writes are done, rather than after each one. See useRefreshSyncedOverviews.
    refreshOverviews();

    // Refetch so the table shows the post-apply state rather than the diff that was just written.
    await preview.refetch();
  };

  if (!canSync) {
    return (
      <Box padding={2}>
        <Alert severity="warning">
          Only Market Access, Admin, Compliance and Risk can sync entities
          against the data warehouse.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: 0,
        padding: 2
      }}
    >
      <Box>
        <Typography variant="body2" color="text.secondary">
          Compares everything that has a Catalyst ID against the data warehouse:
          counterparties, brokers, and each country party separately, since a
          country holds one id per party it depends on. Accepted values are
          written to the entity exactly as the Compliance lifecycle sync writes
          them.
        </Typography>
      </Box>

      {preview.isError && (
        <Alert severity="error">
          Could not read the warehouse comparison.{" "}
          {preview.error instanceof Error ? preview.error.message : ""}
        </Alert>
      )}

      {/* The credit risk view sits behind its own grant and is allowed to fail without failing the
          request. Say so, or every row losing its credit columns reads as "nothing to sync". */}
      {preview.data?.riskSourceUnavailable && (
        <Alert severity="warning">
          The credit risk view could not be read, so credit check, amount and
          currency are missing from every row below. Those columns are unknown
          right now, not in sync. KYC differences are still accurate.
        </Alert>
      )}

      {outcome && (
        <Alert severity={outcome.failed.length ? "warning" : "success"}>
          {`Applied to ${outcome.applied} ${outcome.applied === 1 ? "entity" : "entities"}.`}
          {outcome.failed.length
            ? ` Failed for ${outcome.failed.length}: ${outcome.failed
                .map((failure) => `${failure.label} (${failure.reason})`)
                .join(", ")}.`
            : ""}
        </Alert>
      )}

      {preview.isFetching && !applying ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Reading every counterparty, broker, country and the warehouse...
          </Typography>
        </Box>
      ) : null}

      {applying && (
        <Box>
          <Typography variant="body2" gutterBottom>
            {`Applying ${applied} of ${jobs.length}...`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={jobs.length ? (applied / jobs.length) * 100 : 0}
          />
        </Box>
      )}

      {preview.data && !preview.isFetching && (
        <>
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Typography variant="body2">
              {`${rows.filter((r) => r.actionable.length > 0).length} of ${rows.length} rows with a Catalyst ID differ from the warehouse.`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${preview.data.withoutCatalystId ?? 0} of ${preview.data.totalCounterparties ?? 0} have no Catalyst ID and are not listed.`}
            </Typography>
            {preview.data.notFoundInWarehouse ? (
              <Typography variant="body2" color="warning.main">
                {`${preview.data.notFoundInWarehouse} have a Catalyst ID the warehouse does not know.`}
              </Typography>
            ) : null}
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyDifferences}
                  onChange={(e) => setOnlyDifferences(e.target.checked)}
                />
              }
              label="Only show rows needing attention"
            />
            <Button
              variant="outlined"
              onClick={() => {
                setOutcome(null);
                preview.refetch();
              }}
              disabled={applying}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={applying || jobs.length === 0}
            >
              {`Apply ${fieldCount} change${fieldCount === 1 ? "" : "s"}`}
            </Button>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <SyncTable
              rows={visibleRows}
              selection={selection}
              disabled={applying}
              onToggleField={(row: PreviewRow, field: SyncableField) =>
                setSelection((prev) => toggleField(prev, row, field))
              }
              onToggleRow={(row: PreviewRow, accept: boolean) =>
                setSelection((prev) => toggleRow(prev, row, accept))
              }
            />
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Apply warehouse values?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`This writes ${fieldCount} value${fieldCount === 1 ? "" : "s"} to ${jobs.length} ${jobs.length === 1 ? "entity" : "entities"}. Each one is saved the same way the Compliance lifecycle saves it, and the previous values are kept in blob history.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
